import { useEffect, useState } from "react";
import { DataGrid, GridCol } from "@/components/DataGrid";
import { SyncBar } from "@/components/SyncBar";
import { Tile } from "@/components/Tile";

type Badge = { bg: string; fg: string; label: string };
const SEV: Record<string, Badge> = {
  viral:    { bg: "var(--status-danger-bg)", fg: "var(--status-danger)", label: "Viral" },
  critical: { bg: "var(--status-danger-bg)", fg: "var(--status-danger)", label: "Kritis" },
  medium:   { bg: "var(--status-warn-bg)",   fg: "var(--status-warn)",   label: "Medium" },
  low:      { bg: "var(--gray-100, #F1F5F9)", fg: "var(--color-text-secondary)", label: "Rendah" },
};
const ST: Record<string, Badge> = {
  open:       { bg: "var(--status-danger-bg)",  fg: "var(--status-danger)",  label: "Open" },
  responding: { bg: "var(--status-warn-bg)",    fg: "var(--status-warn)",    label: "Merespons" },
  monitoring: { bg: "var(--status-info-bg)",    fg: "var(--status-info)",    label: "Dipantau" },
  closed:     { bg: "var(--status-success-bg)", fg: "var(--status-success)", label: "Selesai" },
};

export default function IssuesPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  async function load() {
    const r = await fetch("/api/v1/issues");
    if (r.status === 401) { location.href = "/login"; return; }
    setRows((await r.json()).data ?? []);
  }
  useEffect(() => { load(); }, []);
  async function create() {
    if (!title.trim()) return;
    setBusy(true);
    const r = await fetch("/api/v1/issues", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title }) });
    const j = await r.json();
    setBusy(false);
    if (r.ok) location.href = `/a/issues/${j.data.id}`; else alert(j.error);
  }
  const badge = (m: Record<string, Badge>, k: string) => {
    const b = m[k] ?? { bg: "var(--gray-100, #F1F5F9)", fg: "var(--color-text-secondary)", label: k };
    return <span className="avio-chip" data-shape="pill" style={{ background: b.bg, color: b.fg, whiteSpace: "nowrap" }}>{b.label}</span>;
  };
  async function hapusIssue(e: React.MouseEvent, id: string, title: string) {
    e.stopPropagation();
    if (!confirm(`Hapus issue "${title}"? Tautan evidence & counter ikut terhapus.`)) return;
    await fetch(`/api/v1/issues/${id}`, { method: "DELETE" });
    load();
  }
  const open2 = rows.filter((r) => r.status !== "closed");
  const kritis = rows.filter((r) => ["critical", "viral"].includes(r.severity) && r.status !== "closed");
  const withStrat = rows.filter((r) => r.hasStrategy);
  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Issue Management</h1>
          <div className="avio-t-sm avio-muted">Isu yang sedang ditangani beserta bukti dan counter-narasinya</div>
        </div>
      </div>
      <SyncBar />
      <div className="avio-tiles">
        <Tile c={1} icon="fa-solid fa-fire-extinguisher" label="Issue aktif" value={open2.length} sub={`dari ${rows.length} total`} />
        <Tile c={2} icon="fa-solid fa-triangle-exclamation" label="Kritis / viral" value={kritis.length} sub="butuh penanganan cepat" />
        <Tile c={3} icon="fa-solid fa-lightbulb" label="Punya strakom" value={withStrat.length} sub="draf strategi tersedia" />
      </div>
      <div>
        <div className="avio-toolbar">
          <div className="avio-toolbar__row">
            <label className="avio-search">
              <i className="fa-solid fa-plus" />
              <input className="avio-input" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()}
                placeholder="Judul issue baru (mis. Polemik Tarif PNBP AHU)..." />
            </label>
            <button className="avio-btn" data-variant="primary" onClick={create} disabled={busy}>
              <i className="fa-solid fa-plus" /> Buat issue
            </button>
          </div>
        </div>
      <div className="avio-tablewrap">
      <DataGrid
        cols={[
          { id: "title", header: "Issue (klik = detail)", width: "min:220", sortable: true, accessor: (r: any) => r.title, cell: (r: any) => <b style={{ color: "var(--color-text-link)" }}>{r.title}</b> },
          { id: "sev", header: "Severity", width: "max", sortable: true, accessor: (r: any) => r.severity, cell: (r: any) => badge(SEV, r.severity) },
          { id: "saran", header: "Saran Sistem", width: "max", cell: (r: any) => <span title={r.severityWhy ?? ""}>{r.severityAuto ? badge(SEV, r.severityAuto) : "-"}</span> },
          { id: "status", header: "Status", width: "max", sortable: true, accessor: (r: any) => r.status, cell: (r: any) => badge(ST, r.status) },
          { id: "bukti", header: "Bukti", width: "max", sortable: true, accessor: (r: any) => r.stats.total, cell: (r: any) => (
            <span style={{ whiteSpace: "nowrap" }}>
              <b>{r.stats.total}</b>{" "}
              <span className="avio-sub">
                <i className="fa-solid fa-newspaper" /> {r.stats.media} · <i className="fa-solid fa-bullhorn" /> {r.stats.social} ·{" "}
                <span style={{ color: "var(--sent-neg)" }}>{r.stats.negative} neg</span>
              </span>
            </span>) },
          { id: "counter", header: "Counter", width: "max", align: "right", sortable: true, accessor: (r: any) => r.stats.counters, cell: (r: any) => r.stats.counters },
          { id: "strakom", header: "Strakom", width: "max", align: "center", cell: (r: any) => r.hasStrategy ? <i className="fa-solid fa-circle-check" style={{ color: "var(--sent-pos)" }} title="Draf strategi tersedia" /> : <span className="avio-muted">—</span> },
          { id: "upd", header: "Update", width: "max", sortable: true, accessor: (r: any) => r.updatedAt, cell: (r: any) => <span className="avio-sub">{new Date(r.updatedAt).toISOString().slice(0, 10)}</span> },
          { id: "del", header: "", width: "max", cell: (r: any) => <button className="avio-btn" data-variant="ghost" data-size="sm" onClick={(e) => hapusIssue(e as any, r.id, r.title)} title="Hapus issue" style={{ color: "var(--sent-neg)" }}><i className="fa-solid fa-trash" /></button> },
        ] as GridCol<any>[]}
        data={rows}
        onRowClick={(r) => (location.href = `/a/issues/${r.id}`)}
        emptyText="Belum ada issue. Buat dari kotak di atas, atau dari tombol issue di detail artikel/post."
        minTableWidth={900}
      />
      </div>
      </div>
    </div>
  );
}
